<template>
  <main class="rm-page">
    <header class="rm-topbar">
      <div class="rm-heading">
        <h1 class="rm-title">{{ resolvedPlanName }}</h1>
      </div>
    </header>

    <section class="day-route-summary" aria-label="Resumen de ruta">
      <p v-if="summaryText" class="rm-chips">{{ summaryText }}</p>

      <div class="rm-progress-header">
        <span>PROGRESO DE HOY</span>
        <span>{{ progressSummary }}</span>
      </div>

      <div
        class="rm-progress rm-progress--sm"
        role="progressbar"
        :aria-valuemin="0"
        :aria-valuemax="totalStops"
        :aria-valuenow="completedCount"
        :aria-label="progressSummary"
      >
        <span :style="{ width: progressPercent }"></span>
      </div>
    </section>

    <section class="day-route-state-shell" aria-live="polite">
      <div v-if="isLoading" class="day-route-state">
        <span class="day-route-spinner" aria-hidden="true"></span>
        <p>Cargando paradas...</p>
      </div>

      <div v-else-if="hasError" class="day-route-state">
        <h2>No se pudieron cargar las paradas</h2>
        <p>{{ errorMessage }}</p>
        <button
          class="rm-button rm-button--outline"
          type="button"
          :disabled="isBusy"
          @click="loadRouteStops"
        >
          Reintentar
        </button>
      </div>

      <div v-else-if="hasEmptyState" class="day-route-state">
        <h2>Ruta sin paradas</h2>
        <p>{{ emptyMessage }}</p>
        <button
          class="rm-button rm-button--outline"
          type="button"
          :disabled="isBusy"
          @click="loadRouteStops"
        >
          Reintentar
        </button>
      </div>

      <section v-else class="day-route-list" aria-label="Paradas de ruta">
        <article
          v-for="routeStop in routeStops"
          :key="routeStop.key"
          class="route-stop-card"
          :class="{
            'route-stop-card--active': isActive(routeStop),
            'route-stop-card--pending': !routeStop.isClosed,
            'route-stop-card--closed': routeStop.isClosed,
          }"
          tabindex="0"
          role="button"
          :aria-disabled="routeStop.isClosed"
          :aria-expanded="isActive(routeStop)"
          @click="selectStop(routeStop)"
          @keydown.enter.prevent="selectStop(routeStop)"
          @keydown.space.prevent="selectStop(routeStop)"
        >
          <div class="route-stop-card__body">
            <h2>
              {{
                routeStop.stop.inputAddress ||
                routeStop.displayAddress ||
                "Dirección no disponible"
              }}
            </h2>
            <p class="route-stop-card__contact">
              {{ routeStop.contact || "Contacto no disponible" }}
            </p>

            <div class="route-stop-card__schedule">
              <span>{{ routeStop.scheduleLabel }}</span>
            </div>
          </div>

          <div v-if="showActions(routeStop)" class="route-stop-card__actions">
            <button
              class="rm-button rm-button--outline route-stop-card__start"
              type="button"
              @click.stop="openServiceDetail(routeStop)"
            >
              <img
                class="rm-button__icon-img"
                :src="sendIcon"
                alt=""
                aria-hidden="true"
              />
              <span>Iniciar</span>
            </button>
          </div>
        </article>
      </section>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { mobileRouteStore } from "@/_store/mobileRouteStore";
import sendIcon from "../../../../assets/img/mobile-images/send.svg";
import {
  buildSummaryChips,
  completedRouteStopCount,
  createRouteStopItem,
  sortRouteStops,
} from "./dayRouteModel";

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
  context: {
    type: Object,
    default() {
      return null;
    },
  },
});
const emit = defineEmits(["navigate"]);
const defaultPlanName = "Ruta del día";
const mobileRoutesStore = mobileRouteStore();

const planId = ref("");
const planName = ref("");
const routeStops = ref([]);
const activeStopKey = ref("");
const isLoading = ref(false);
const isRefreshing = ref(false);
const errorMessage = ref("");
const emptyMessage = ref("");
let abortController = null;

const resolvedPlanName = computed(() => planName.value || defaultPlanName);
const isBusy = computed(() => isLoading.value || isRefreshing.value);
const hasRouteStops = computed(() => routeStops.value.length > 0);
const hasError = computed(
  () => !isBusy.value && !hasRouteStops.value && Boolean(errorMessage.value)
);
const hasEmptyState = computed(
  () =>
    !isBusy.value &&
    !hasError.value &&
    !hasRouteStops.value &&
    Boolean(emptyMessage.value)
);
const completedCount = computed(() =>
  completedRouteStopCount(routeStops.value)
);
const totalStops = computed(() => routeStops.value.length);
const progressSummary = computed(
  () => `${completedCount.value} / ${totalStops.value} COMPLETADAS`
);
const progressPercent = computed(() => {
  if (totalStops.value <= 0) {
    return "0%";
  }

  return `${Math.min(
    100,
    Math.max(0, (completedCount.value / totalStops.value) * 100)
  )}%`;
});
const summaryText = computed(() =>
  buildSummaryChips(routeStops.value).join(" · ")
);

onMounted(() => {
  readRouteQuery();
  loadRouteStops();
});

onBeforeUnmount(() => {
  abortCurrentRequest();
});

async function loadRouteStops({ isRefresh = false } = {}) {
  if (isBusy.value) {
    return;
  }

  if (!planId.value) {
    errorMessage.value = "No se recibio el identificador del plan en la URL.";
    emptyMessage.value = "";
    routeStops.value = [];
    return;
  }

  const vehicleId = (props.context?.vehicleId || "").toString();

  if (!vehicleId) {
    errorMessage.value = "No se encontro vehicleId para consultar la ruta.";
    emptyMessage.value = "";
    routeStops.value = [];
    return;
  }

  abortCurrentRequest();
  abortController = new AbortController();

  if (isRefresh) {
    isRefreshing.value = true;
  } else {
    isLoading.value = true;
    routeStops.value = [];
    activeStopKey.value = "";
  }

  errorMessage.value = "";
  emptyMessage.value = "";

  try {
    const response = await mobileRoutesStore.loadRouteStopsByPlan({
      planId: planId.value,
      vehicleId,
      signal: abortController.signal,
    });

    routeStops.value = sortRouteStops(response.map(createRouteStopItem));

    if (routeStops.value.length === 0) {
      emptyMessage.value = "No hay paradas asignadas para esta ruta.";
    }
  } catch (error) {
    if (error.name === "AbortError") {
      return;
    }

    if (!hasRouteStops.value) {
      routeStops.value = [];
      activeStopKey.value = "";
    }

    errorMessage.value =
      error.message || "No se pudieron cargar las paradas del plan.";
  } finally {
    isRefreshing.value = false;
    isLoading.value = false;
    abortController = null;
  }
}

function selectStop(routeStop) {
  if (routeStop.isClosed) {
    return;
  }

  activeStopKey.value = isActive(routeStop) ? "" : routeStop.key;
}

function showActions(routeStop) {
  return !routeStop.isClosed && isActive(routeStop);
}

function isActive(routeStop) {
  return activeStopKey.value === routeStop.key;
}

function openServiceDetail(routeStop) {
  const stopId = routeStop?.stop?.stopId || "";
  if (!stopId) {
    return;
  }

  if (props.embedded) {
    emit("navigate", {
      view: "service-detail",
      payload: {
        planId: planId.value,
        routeStopId: stopId,
        stopId,
        routeStop,
      },
    });
    return;
  }

  const target = new URL("/service-detail", window.location.origin);
  target.searchParams.set("planId", planId.value);
  target.searchParams.set("stopId", stopId);
  window.location.assign(target.toString());
}

function readRouteQuery() {
  if (props.embedded) {
    planId.value = props.context?.planId?.trim?.() ?? "";
    planName.value = props.context?.planName?.trim?.() ?? "";
    return;
  }

  const query = new URLSearchParams(window.location.search);
  planId.value = query.get("planId")?.trim() ?? "";
  planName.value = query.get("planName")?.trim() ?? "";
}

function abortCurrentRequest() {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
}
</script>

<style scoped src="./dayRoute.css"></style>
<style scoped src="../shared/routesMobileCommon.css"></style>
